import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  CardBody,
  Checkbox,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { accountApi } from '@netiva/classifieds-api';
import { usePropChange } from '@netiva/classifieds-common';
import { SelectableCard } from '@netiva/classifieds-ui';

import { normalizeProductGroupName, useMatomoTagManager, useProductGroups } from '@/hooks';
import { ProductGroups } from '@/constants';
import { canUpgradeTo } from '../utils';
import { Contract, Product } from '../types';
import { SelectableRadio } from './SelectableRadio';
import { TopListingBadge } from './TopListingBadge';
import { TopListingBenefits } from './TopListingBenefits';
import { TopListingNotification } from './TopListingNotification';
import { Price } from './Price';

export type UpgradeModalProps = Omit<ModalProps, 'children'> & {
  contract?: Contract;
  onSuccess?: () => void;
  products: Product[];
  areAllTopListingSlotsUsed?: boolean;
};

export const UpgradeModal: FC<UpgradeModalProps> = ({
  areAllTopListingSlotsUsed = false,
  contract,
  onSuccess,
  products,
  isOpen,
  onClose,
  ...rest
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { trackMatomoEvent } = useMatomoTagManager();

  const availableProducts = useMemo(
    () => products.filter((p) => contract?.product && canUpgradeTo(contract.product, p)),
    [contract, products]
  );

  const { orderedProductGroups } = useProductGroups(availableProducts);

  const isTopListingProduct = orderedProductGroups.some(
    ([group]) => group === normalizeProductGroupName(ProductGroups.MemberTopPlacement)
  );

  const [selectedProduct, setSelectedProduct] = useState<Product>();
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [upgradeContract, { isLoading: isUpgradingContract }] = accountApi.useUpgradeContract();

  if (usePropChange(isOpen)) {
    setSelectedProduct(undefined);
    setIsConfirmed(false);
  }

  const handleSubmit = async () => {
    if (!contract || !selectedProduct || !isConfirmed) {
      return;
    }

    try {
      await upgradeContract({
        accountContractsUpgradeContractRequest: { contractId: contract.id, productId: selectedProduct.id },
      }).unwrap();
      trackMatomoEvent({
        trackingCategory: 'Interaction_PI-Products',
        trackingAction: 'Upgrade_Product',
        trackingName: `Upgrade_Product_${selectedProduct.sku ?? `${selectedProduct.group}_${selectedProduct.numberOfAds}`}`,
      });
      toast({ status: 'success', description: t('products.upgradeModal.success') });
      onClose();
      onSuccess?.();
    } catch (err) {
      console.error(err);
      toast({ status: 'error', description: t('products.upgradeModal.error') });
    }
  };

  return (
    <Modal isCentered scrollBehavior="inside" size="xl" isOpen={isOpen} onClose={onClose} {...rest}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text>{t('products.upgradeModal.title')}</Text>
          <ModalCloseButton />
        </ModalHeader>
        <ModalBody>
          <div>
            <Text>{t('products.upgradeModal.currentProduct')}</Text>
            <Text>{contract?.product?.name}</Text>
          </div>
          <Accordion
            defaultIndex={orderedProductGroups.length === 1 ? 0 : undefined}
            allowToggle={orderedProductGroups.length > 1}
            mt={4}
          >
            {orderedProductGroups.map(([group, products]) => (
              <AccordionItem key={group}>
                <AccordionButton cursor={orderedProductGroups.length === 1 ? 'default' : undefined}>
                  <VStack align="stretch" textAlign="start" py={1}>
                    <Heading size="md">
                      {isTopListingProduct && areAllTopListingSlotsUsed
                        ? t('products.upgradeModal.allTopListingSlotsUsed.title')
                        : t(`ad.steps.publishing.productGroups.${group}.title`, '')}
                    </Heading>
                    <Text>
                      {isTopListingProduct && areAllTopListingSlotsUsed
                        ? t('products.upgradeModal.allTopListingSlotsUsed.description')
                        : t(`ad.steps.publishing.productGroups.${group}.description`, '')}
                    </Text>
                  </VStack>
                  {orderedProductGroups.length > 1 && <AccordionIcon ml="auto" boxSize={8} />}
                </AccordionButton>
                <AccordionPanel>
                  <VStack align="stretch" spacing={4}>
                    {isTopListingProduct && <TopListingNotification />}
                    {products.map((p) => (
                      <Box key={p.id} position="relative">
                        {p.numberOfAds === 5 && <TopListingBadge />}
                        <SelectableCard isSelected={p.id === selectedProduct?.id} onClick={() => setSelectedProduct(p)}>
                          <CardBody p={0}>
                            <Flex justifyContent="space-between" gap={2} align="flex-start">
                              <Flex gap={3} align="center">
                                <SelectableRadio isSelected={p.id === selectedProduct?.id} />
                                <Text fontSize="lg" fontWeight="bold">
                                  {p.name}
                                </Text>
                              </Flex>
                              <Price price={p.price} />
                            </Flex>
                            {!isTopListingProduct && p.description && !p.description.startsWith('#') && (
                              <Text dangerouslySetInnerHTML={{ __html: p.description }} />
                            )}
                          </CardBody>
                        </SelectableCard>
                      </Box>
                    ))}
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </ModalBody>
        <ModalFooter display="block">
          <Text mb={4}>{t('products.upgradeModal.footer')}</Text>
          {isTopListingProduct && <TopListingBenefits />}
          <Checkbox isChecked={isConfirmed} onChange={(e) => setIsConfirmed(e.target.checked)} mb={4}>
            {t('products.upgradeModal.termsConfirmation')}
          </Checkbox>
          <Flex>
            <Button onClick={onClose} colorScheme="neutral">
              {t('ui.buttons.cancel')}
            </Button>
            <Button
              onClick={handleSubmit}
              isLoading={isUpgradingContract}
              isDisabled={!selectedProduct || !isConfirmed}
              ml="auto"
            >
              {selectedProduct?.numberOfAds
                ? t('products.upgradeModal.submitWithCount', { count: selectedProduct.numberOfAds })
                : t('products.upgradeModal.submit')}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
